import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  H3,
  Panel,
  PanelBody,
  PanelHeader,
  PanelSection,
  PanelSectionTitle,
  PanelTitle,
  StatusLabel,
  WarningNotice,
} from '@studio/legacy-components';
import { FLOW_FREQUENCY, selectFeature } from 'next/entities/features';
import { selectAccount, update as updateAccount } from 'next/entities/account';
import FlowFrequencyModalManager, {
  getCurrentTimeframe,
} from 'next/components/FlowFrequencyModalManager';
import { CheckboxLabel, Row, SectionCheckbox } from './styled';

export function FlowFrequency({
  hasFrequency,
  onRuleChange,
  onUpdateAccount,
  throttleExempt,
  throttleEnabled,
  throttleContentLimit,
  throttleContentTimespanMs,
}) {
  const currentTimeframe = getCurrentTimeframe(throttleContentTimespanMs);

  if (!hasFrequency) return null;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>
          <H3>Flow frequency</H3>
        </PanelTitle>
      </PanelHeader>

      <PanelBody>
        <PanelSection>
          <Row>
            <PanelSectionTitle>
              You can limit how often users can see a Flow.
            </PanelSectionTitle>
            <FlowFrequencyModalManager
              onSave={onUpdateAccount}
              throttleEnabled={throttleEnabled}
              throttleContentLimit={throttleContentLimit}
              throttleContentTimespanMs={throttleContentTimespanMs}
            />
          </Row>
          <PanelSectionTitle>
            Flow frequency is:
            <StatusLabel
              label={throttleEnabled ? 'On' : 'Off'}
              icon={throttleEnabled ? 'check-circle' : 'minus-circle'}
              active={throttleEnabled}
            />
          </PanelSectionTitle>
          {throttleEnabled && (
            <WarningNotice>
              Your users might not see this Flow right away. This Flow will not
              be shown to a user if they have seen more than{' '}
              <strong>
                {throttleContentLimit || '-'} per {currentTimeframe}
              </strong>
              . After the {currentTimeframe}, they will show again.
            </WarningNotice>
          )}
        </PanelSection>
        <SectionCheckbox
          disabled={!throttleEnabled}
          defaultChecked={throttleExempt}
          onChange={isChecked => {
            onRuleChange({
              throttleExempt: isChecked,
            });
          }}
        >
          <CheckboxLabel>Override frequency limit on this Flow</CheckboxLabel>
        </SectionCheckbox>
      </PanelBody>
    </Panel>
  );
}

FlowFrequency.propTypes = {
  hasFrequency: PropTypes.bool,
  onRuleChange: PropTypes.func,
  onUpdateAccount: PropTypes.func,
  throttleExempt: PropTypes.bool,
  throttleEnabled: PropTypes.bool,
  throttleContentLimit: PropTypes.number,
  throttleContentTimespanMs: PropTypes.number,
};

const mapStateToProps = state => {
  const account = selectAccount(state);

  return {
    hasFrequency: selectFeature(state, FLOW_FREQUENCY),
    throttleEnabled: account?.throttleEnabled,
    throttleContentLimit: account?.throttleContentLimit,
    throttleContentTimespanMs: account?.throttleContentTimespanMs,
  };
};

const mapDispatchToProps = {
  onUpdateAccount: updateAccount,
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowFrequency);
